import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateAfterAnswerSave, invalidateAfterAssessmentSubmit } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import {
  ATTEMPT_QUESTIONNAIRE_ERROR,
  adaptAttemptQuestionnaire,
  answersMap,
  firstIncompleteGroupIndex,
  firstUnansweredQuestion,
  questionIdentity,
  questionRangeForGroup,
  selectedAnswersPayload,
} from 'features/assessment/attemptQuestionnaire';
import { userApi } from 'features/home/home.api';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { Button } from 'shared/components/Button';
import { ErrorState, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { ProgressIndicator } from 'shared/components/PatternBar';
import { localizedText } from 'shared/utilities/format';

function questionnaireErrorMessage(error, t) {
  if (error === ATTEMPT_QUESTIONNAIRE_ERROR.EMPTY) {
    return t('assessment.empty');
  }
  return t('assessment.invalid');
}

function attemptLoadErrorMessage(error, t) {
  if (error instanceof ApiError && (error.isNotFound() || error.isUnauthorized() || error.isForbidden())) {
    return t('assessment.stale');
  }
  return humanizeApiError(error, t);
}

function focusQuestion(question) {
  const identity = questionIdentity(question);
  if (identity == null) {
    return;
  }
  const node = document.getElementById(`question-${identity}`);
  if (!node) {
    return;
  }
  if (typeof node.scrollIntoView === 'function') {
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  const radio = node.querySelector('input[type="radio"]');
  if (radio) {
    radio.focus();
  } else {
    node.focus();
  }
}

function QuestionCard({ question, selectedId, onSelect, locale, t, markedInvalid }) {
  const identity = questionIdentity(question);
  const headingId = `question-${identity}-title`;
  return (
    <section
      className={markedInvalid ? 'card question-card is-invalid' : 'card question-card'}
      id={`question-${identity}`}
      data-testid={`question-${identity}`}
      data-question-id={identity}
      tabIndex={-1}
      aria-labelledby={headingId}
    >
      <h2 id={headingId}>{localizedText(question, locale, ['text', 'title'])}</h2>
      {question.explanation ? <p className="muted">{localizedText(question, locale, ['explanation'])}</p> : null}
      <fieldset className="answer-set">
        <legend className="sr-only">{t('assessment.chooseAnswer')}</legend>
        {(question.answers || []).map((answer) => {
          const answerId = `answer-${identity}-${answer.id}`;
          const checked = Number(selectedId) === Number(answer.id);
          return (
            <label key={answer.id} className={checked ? 'answer is-selected' : 'answer'} htmlFor={answerId}>
              <input
                id={answerId}
                type="radio"
                name={`question-${identity}`}
                checked={checked}
                onChange={() => onSelect(identity, answer.id)}
              />
              <span>{localizedText(answer, locale, ['text', 'title'])}</span>
            </label>
          );
        })}
      </fieldset>
    </section>
  );
}

export function AssessmentPage() {
  const { attemptId } = useParams();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const savingRef = useRef(false);
  const submittingRef = useRef(false);
  const nextLockRef = useRef(false);
  const initializedAttemptRef = useRef(null);
  const [groupIndex, setGroupIndex] = useState(0);
  const [selected, setSelected] = useState({});
  const [invalidQuestionId, setInvalidQuestionId] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const query = useQuery({
    queryKey: queryKeys.attempt(attemptId),
    queryFn: () => userApi.showAttempt(attemptId),
  });

  const adapted = useMemo(() => adaptAttemptQuestionnaire(query.data), [query.data]);
  const questionnaire = adapted.ok ? adapted.data : null;
  const attempt = questionnaire?.attempt || query.data?.attempt || query.data;
  const groups = questionnaire?.question_groups || [];
  const currentGroup = groups[groupIndex] || null;
  const sponsorCompanyName = attempt?.sponsoring_company_name
    || attempt?.sponsor?.company_name
    || attempt?.company_name
    || null;
  const isSponsored = Boolean(attempt?.is_sponsored || sponsorCompanyName);

  useEffect(() => {
    if (!questionnaire || !attempt) {
      return;
    }
    const serverAnswers = answersMap(attempt.selected_answers);
    const attemptKey = String(questionnaire.attempt_id || attempt.id || attemptId);
    if (initializedAttemptRef.current !== attemptKey) {
      initializedAttemptRef.current = attemptKey;
      setSelected(serverAnswers);
      setGroupIndex(firstIncompleteGroupIndex(questionnaire.question_groups, attempt.selected_answers));
      return;
    }
    setSelected((current) => ({ ...serverAnswers, ...current }));
  }, [questionnaire, attempt, attemptId]);

  useEffect(() => {
    if (query.isError && query.error instanceof ApiError && query.error.isConflict() && attemptId) {
      navigate(paths.result(attemptId), { replace: true });
    }
  }, [query.isError, query.error, attemptId, navigate]);

  useEffect(() => {
    if (attempt?.status === 'completed' && attemptId) {
      navigate(paths.result(attemptId), { replace: true });
    }
  }, [attempt?.status, attemptId, navigate]);

  function selectAnswer(questionId, answerId) {
    setInvalidQuestionId(null);
    setSelected((current) => ({ ...current, [questionId]: Number(answerId) }));
  }

  async function saveCurrentGroup() {
    if (!currentGroup) {
      return false;
    }
    if (savingRef.current) {
      return false;
    }
    savingRef.current = true;
    setSaveError('');
    try {
      await userApi.saveAnswers(attemptId, selectedAnswersPayload(currentGroup.questions, selected));
      await invalidateAfterAnswerSave(queryClient, attemptId);
      return true;
    } catch (err) {
      setSaveError(humanizeApiError(err, t));
      return false;
    } finally {
      savingRef.current = false;
    }
  }

  async function goNext() {
    if (!currentGroup || submitting || nextLockRef.current) {
      return;
    }
    const unanswered = firstUnansweredQuestion(currentGroup.questions, selected);
    if (unanswered) {
      setInvalidQuestionId(questionIdentity(unanswered));
      focusQuestion(unanswered);
      return;
    }
    nextLockRef.current = true;
    const saved = await saveCurrentGroup();
    if (!saved) {
      nextLockRef.current = false;
      return;
    }
    if (groupIndex < groups.length - 1) {
      setGroupIndex((current) => current + 1);
      nextLockRef.current = false;
      return;
    }
    const ok = await confirm({
      title: t('assessment.submitTitle'),
      body: t('assessment.submitBody'),
      confirmLabel: t('actions.submit'),
      tone: 'primary',
      action: async () => {
        if (submittingRef.current) {
          return;
        }
        submittingRef.current = true;
        setSubmitting(true);
        try {
          const result = await userApi.submitAttempt(attemptId);
          await invalidateAfterAssessmentSubmit(queryClient, result.attempt_id || attemptId);
          navigate(paths.result(result.attempt_id || attemptId), { replace: true });
        } catch (err) {
          setSaveError(humanizeApiError(err, t));
          submittingRef.current = false;
          setSubmitting(false);
          nextLockRef.current = false;
        }
      },
    });
    if (!ok) {
      nextLockRef.current = false;
    }
  }

  function goPrevious() {
    if (groupIndex === 0) {
      return;
    }
    setGroupIndex((current) => Math.max(0, current - 1));
  }

  if (query.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.isConflict()) {
      return <Skeleton rows={4} />;
    }
    return (
      <ErrorState
        title={attemptLoadErrorMessage(query.error, t)}
        onRetry={() => query.refetch()}
        retryLabel={t('actions.retry')}
      />
    );
  }
  if (attempt?.status === 'completed') {
    return <Skeleton rows={4} />;
  }
  if (!adapted.ok) {
    return <ErrorState title={questionnaireErrorMessage(adapted.error, t)} homeAction />;
  }
  if (!currentGroup) {
    return <ErrorState title={t('assessment.empty')} />;
  }

  const range = questionRangeForGroup(groups, groupIndex, questionnaire.total_questions);
  const displayedGroupNumber = currentGroup.group_number || groupIndex + 1;
  const progressLabel = t('assessment.questionRange', {
    start: range.start,
    end: range.end,
    total: range.total,
  });
  const isLastGroup = groupIndex === groups.length - 1;

  return (
    <div className="assessment-page" data-testid="assessment-question-group">
      <PageMeta titleKey="meta.assessment" />
      {isSponsored && sponsorCompanyName ? (
        <div className="sponsored-banner" role="note">
          <StatusChip tone="accent">{t('assessment.sponsoredBadge', { companyName: sponsorCompanyName })}</StatusChip>
        </div>
      ) : null}
      <h1 className="assessment-group-title">{progressLabel}</h1>
      <p className="muted" data-testid="assessment-group-progress">
        {t('assessment.groupProgress', { current: displayedGroupNumber, total: questionnaire.total_groups })}
      </p>
      <ProgressIndicator
        current={range.end}
        total={range.total}
        label={progressLabel}
      />
      <div className="question-group" data-testid="question-group">
        {currentGroup.questions.map((question) => {
          const identity = questionIdentity(question);
          return (
            <QuestionCard
              key={identity}
              question={question}
              selectedId={selected[identity] ?? selected[Number(identity)]}
              onSelect={selectAnswer}
              locale={locale}
              t={t}
              markedInvalid={String(invalidQuestionId) === String(identity)}
            />
          );
        })}
      </div>
      {saveError ? <p className="field-error" role="alert">{saveError}</p> : null}
      {submitting ? <p role="status">{t('assessment.submitting')}</p> : null}
      <div className="question-actions">
        <Button tone="ghost" onClick={goPrevious} disabled={groupIndex === 0}>
          {t('actions.previous')}
        </Button>
        <Button onClick={goNext} disabled={submitting} loading={submitting}>
          {isLastGroup ? t('actions.submit') : t('actions.next')}
        </Button>
      </div>
    </div>
  );
}
